export type MeetingSource = 'microphone' | 'meeting';
export interface MeetingCapture {
  stream: MediaStream;
  tracks: MediaStreamTrack[];
  close: () => void;
}

/** Mix only audio into the recorder. Screen video is never recorded or uploaded. */
export async function captureMeeting(source: MeetingSource, signal: AbortSignal): Promise<MeetingCapture> {
  const streams: MediaStream[] = [];
  let context: AudioContext | undefined;
  const close = () => {
    streams.forEach((s) => s.getTracks().forEach((t) => t.stop()));
    if (context) void context.close().catch(() => undefined);
    signal.removeEventListener('abort', close);
  };
  signal.addEventListener('abort', close, { once: true });
  const check = () => {
    if (signal.aborted) throw new DOMException('Recording cancelled', 'AbortError');
  };
  try {
    check();
    // Screen sharing needs transient activation, so request it before any other await.
    if (source === 'meeting') {
      const shared = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      streams.push(shared);
      check();
      if (!shared.getAudioTracks().length) throw new Error('No meeting audio was shared. Select the meeting tab and enable Share audio, or use microphone-only recording.');
      // Keep the display track alive so Stop sharing ends capture, but never feed it to MediaRecorder.
    }
    const mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true }, video: false });
    streams.push(mic);
    check();
    if (streams.some((s) => s.getTracks().some((t) => t.readyState === 'ended'))) throw new Error('The audio source disconnected before recording started.');
    if (source === 'microphone') return { stream: mic, tracks: mic.getTracks(), close };
    context = new AudioContext();
    await context.resume();
    check();
    const destination = context.createMediaStreamDestination();
    for (const s of streams) context.createMediaStreamSource(new MediaStream(s.getAudioTracks())).connect(destination);
    const tracks = streams.flatMap((s) => s.getTracks());
    streams.push(destination.stream);
    return { stream: destination.stream, tracks, close };
  } catch (error) {
    close();
    throw error;
  }
}
